import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { useTranslation } from 'react-i18next';
import { communicationStatus } from '~client/constants';
import { Table } from 'antd';

import { formatDay, dayFormatType } from 'common/helpers/day';
import formatPrice from '~helpers/currency';
import generatePath from '~settings/routing';
import { showPassportName } from '~helpers';
import { bookingJourneyType } from '~constants/index';

const ReconciliationTable = ({ filters, list, bookingJourney, history, handleTableChange }) => {
  const { t } = useTranslation();

  const opportunityColumns = [
    {
      title: t('landlord.reconciliation_list.table.student_name.label'),
      dataIndex: 'studentName',
      key: 'studentName',
      width: 160,
      className: 'reconciliation-list__table-column',
      render: (_, record) => <span>{showPassportName(get(record, 'node.student')) || '-'}</span>,
    },
    {
      title: t('landlord.reconciliation_list.table.student_email.label'),
      dataIndex: 'studentEmail',
      key: 'studentEmail',
      width: 200,
      className: 'reconciliation-list__table-column',
      render: (_, record) => <span>{get(record, 'node.student.emailAddress') || '-'}</span>,
    },
    {
      title: t('landlord.reconciliation_list.table.student_booking_id.label'),
      dataIndex: 'referenceId',
      key: 'referenceId',
      width: 160,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>{get(record, 'node.completedXbooking.referenceId') || '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.partner_booking_id.label'),
      dataIndex: 'externalId',
      key: 'externalId',
      width: 160,
      className: 'reconciliation-list__table-column',
      render: (_, record) => <span>{get(record, 'node.landlordApplicationId') || '-'}</span>,
    },
    {
      title: t('landlord.reconciliation_list.table.room_name.label'),
      dataIndex: 'roomName',
      key: 'roomName',
      width: 160,
      className: 'reconciliation-list__table-column',
      render: (_, record) => <span>{get(record, 'node.completedXbooking.roomName') || '-'}</span>,
    },
    {
      title: t('landlord.reconciliation_list.table.property_name.label'),
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: 200,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>{get(record, 'node.completedXbooking.propertyName') || '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.move_in_date.label'),
      key: 'checkInDate',
      dataIndex: 'checkInDate',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.finalMoveInDate')
            ? moment(get(record, 'node.finalMoveInDate')).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.move_out_date.label'),
      key: 'checkOutDate',
      dataIndex: 'checkOutDate',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.finalMoveOutDate')
            ? moment(get(record, 'node.finalMoveOutDate')).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.complete_at.label'),
      key: 'completedAt',
      dataIndex: 'completedAt',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.completedAt')
            ? moment(get(record, 'node.completedAt')).format('DD/MM/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.commission_value.label'),
      key: 'totalCommissionValue',
      dataIndex: 'totalCommissionValue',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.completedXbooking.commission.totalCommissionValue')
            ? formatPrice(
                get(record, 'node.completedXbooking.commission.totalCommissionValue'),
                get(record, 'node.completedXbooking.commission.currency'),
              )
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.total_rent.label'),
      key: 'totalRent',
      dataIndex: 'totalRent',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.finalPrice')
            ? formatPrice(get(record, 'node.finalPrice'), get(record, 'node.currency'))
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.status.label'),
      key: 'landlordBookingStatus',
      dataIndex: 'landlordBookingStatus',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.landlordBookingStatus')
            ? t(
                `landlord.reconciliation_list.table.status.${toLower(
                  get(record, 'node.landlordBookingStatus'),
                )}`,
              )
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.invoice_number.label'),
      key: 'invoiceNumber',
      dataIndex: 'invoiceNumber',
      width: 156,
      className: 'reconciliation-list__table-column',
      render: (_, record) => (
        <span>
          {get(record, 'node.completedXbooking.commission.invoiceNumber')
            ? get(record, 'node.completedXbooking.commission.invoiceNumber')
            : '-'}
        </span>
      ),
    },
  ];

  const orderColumns = [
    {
      title: t('landlord.reconciliation_list.table.booking_number.label'),
      dataIndex: 'referenceId',
      key: 'referenceId',
      width: 160,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.order?.referenceId ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.property.label'),
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: 200,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.order?.propertyName ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.destination_country.label'),
      dataIndex: 'destinationCountry',
      key: 'destinationCountry',
      width: 200,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.order?.city?.country?.name ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.destination_city.label'),
      dataIndex: 'destinationCity',
      key: 'destinationCity',
      width: 200,
      render: (_, record) => (
        <div className="transfer__table-field">{record?.order?.city?.name ?? '-'}</div>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.students_name.label'),
      dataIndex: 'studentName',
      key: 'studentName',
      width: 176,
      render: (_, record) => (
        <div className="transfer__table-field">
          {`${record?.order?.student?.passportFirstName} ${
            record?.order?.student?.passportMiddleName ?? ''
          } ${record?.order?.student?.passportLastName}` ?? '-'}
        </div>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.currency.label'),
      dataIndex: 'currency',
      key: 'currency',
      width: 144,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.currency ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.total_rent.label'),
      dataIndex: 'totalRent',
      key: 'totalRent',
      width: 150,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.order?.totalRental ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.commission_tier.label'),
      dataIndex: 'commissionTier',
      key: 'commissionTier',
      width: 144,
      render: (_, record) => (
        <span className="transfer__table-field">
          <Choose>
            <When condition={record?.commission?.commissionTierValue}>
              {record?.commission?.commissionTierValue &&
                (record?.commission?.commissionTierType === 'PERCENTAGE'
                  ? record?.commission?.commissionTierValue + '%'
                  : record?.commission?.commissionTierValue)}
            </When>
            <Otherwise>-</Otherwise>
          </Choose>
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.total_commission.label'),
      dataIndex: 'totalCommission',
      key: 'totalCommission',
      width: 156,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.commission?.totalPrice ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.move_in_date.label'),
      dataIndex: 'moveInDate',
      key: 'moveInDate',
      width: 156,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.order?.moveInDate
            ? formatDay(dayFormatType.UKFORMAT, record?.order?.moveInDate)
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.move_out_date.label'),
      dataIndex: 'moveOutDate',
      key: 'moveOutDate',
      width: 156,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.order?.moveOutDate
            ? formatDay(dayFormatType.UKFORMAT, record?.order?.moveOutDate)
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.current_status.label'),
      dataIndex: 'status',
      key: 'status',
      width: 184,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.status
            ? t(`landlord.reconciliation_list.table.status.${record.status.toLowerCase()}`)
            : '-'}
        </span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.invoice_number.label'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      width: 150,
      render: (_, record) => (
        <span className="transfer__table-field">{record?.invoiceNumber ?? '-'}</span>
      ),
    },
    {
      title: t('landlord.reconciliation_list.table.invoice_at.label'),
      dataIndex: 'invoicedAt',
      key: 'invoicedAt',
      width: 150,
      render: (_, record) => (
        <span className="transfer__table-field">
          {record?.invoicedAt ? formatDay(dayFormatType.UKFORMAT, record?.invoicedAt) : '-'}
        </span>
      ),
    },
  ];

  const columns = bookingJourney === bookingJourneyType.MANUAL ? opportunityColumns : orderColumns;

  const handleRowClick = record => {
    history.push({
      pathname: generatePath('reconciliation.detail', {
        id: record.node ? record.node.id : record.id,
        bookingJourney,
      }),
      state: {
        record: record.node ? record.node : record,
        bookingJourney,
      },
    });
  };

  return (
    <Table
      className="reconciliation-list__table"
      rowClassName={() => 'reconciliation-list__row'}
      scroll={{
        x: '100%',
        scrollToFirstRowOnChange: true,
      }}
      columns={columns}
      rowKey={record => record.id}
      dataSource={list.payload}
      loading={get(list, 'communication.status') === communicationStatus.FETCHING}
      pagination={{
        current: filters.pageNumber,
        pageSize: filters.pageSize,
        showSizeChanger: true,
        hideOnSinglePage: false,
        total: list.totalCount,
      }}
      onChange={handleTableChange}
      onRow={record => ({
        onClick: () => {
          handleRowClick(record);
        },
      })}
    />
  );
};

ReconciliationTable.propTypes = {
  list: PropTypes.object,
  filters: PropTypes.object,
  handlePaginationChange: PropTypes.func,
  handlePageSizeChange: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

ReconciliationTable.defaultProps = {
  list: {
    payload: [],
  },
  filters: {},
  handlePaginationChange: () => {},
  handlePageSizeChange: () => {},
  history: {
    push: () => {},
  },
};

export default ReconciliationTable;
